import React, { useEffect, useMemo, useState } from "react"
import { Activity, TrendingUp, TrendingDown, Lightbulb, AlertTriangle, BarChart3 } from "lucide-react"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts"
import { ReportCard, ReportSectionHeader, ReportStatTile, ReportEmptyState, ReportSkeleton, ReportBadge } from "../components/ReportUI"
import { REPORT_COLORS } from "../components/reportTheme"
import { ai_api } from "../../../utils/variables"

const fmt = d => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" })
const fmtNum = n =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : Math.round(n).toLocaleString()
const fmtCurrency = n => `$${fmtNum(n)}`

const fetchForecast = async appId => {
  const token = localStorage.getItem("authToken")
  const headers = { "Content-Type": "application/json" }
  if (token) headers.Authorization = `Bearer ${token}`
  const response = await fetch(`${ai_api}/forecasting/forecast/`, {
    method: "POST",
    headers,
    body: JSON.stringify({ app_id: appId }),
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  return response.json()
}

const ChartTooltip = ({ active, payload, label, isCurrency }) => {
  if (!active || !payload?.length) return null
  const point = payload.find(p => p.value != null)
  if (!point) return null
  return (
    <div style={{ backgroundColor: REPORT_COLORS.cardBgRaised, border: `1px solid ${REPORT_COLORS.cardBorder}`, borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
      <div style={{ color: REPORT_COLORS.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ color: REPORT_COLORS.textPrimary, fontWeight: 600, fontFamily: "monospace" }}>
        {isCurrency ? fmtCurrency(point.value) : fmtNum(point.value)}
      </div>
    </div>
  )
}

export default function AIForecastingSummaryTab({ appId, appName }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  useEffect(() => {
    if (!appId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchForecast(appId)
      .then(result => {
        if (cancelled) return
        if (result.status === 1 && result.data?.actual_downloads?.length) {
          setData(result.data)
        } else if (result.status === 1) {
          setData(null)
          setError("empty")
        } else {
          setError("Failed to load forecast data")
        }
      })
      .catch(err => !cancelled && setError(err.message || "Failed to load forecast data"))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [appId])

  const dlChart = useMemo(() => {
    if (!data) return []
    const recent = (data.actual_downloads || []).slice(-30)
    const last = recent[recent.length - 1]
    const forecast = data.downloads_forecast || []
    return [
      ...recent.map(d => ({ date: fmt(d.date), actual: d.value, forecast: undefined })),
      ...(last ? [{ date: fmt(last.date), actual: last.value, forecast: last.value }] : []),
      ...forecast.map(d => ({ date: fmt(d.date), actual: undefined, forecast: d.value })),
    ]
  }, [data])

  if (!appId) return null

  if (loading) return <ReportSkeleton tiles={4} />

  if (error === "empty") {
    return <ReportEmptyState icon={Activity} title="No Forecast Data Available" description="Forecast data hasn't been generated for this app yet." />
  }

  if (error) {
    return <ReportEmptyState icon={AlertTriangle} title="Failed to Load Forecast" description={error} />
  }

  if (!data) return null

  const lastDl = data.actual_downloads?.[data.actual_downloads.length - 1]?.value || 0
  const forecastEndDl = data.downloads_forecast?.[data.downloads_forecast.length - 1]?.value || 0
  const dlGrowth = lastDl > 0 ? ((forecastEndDl - lastDl) / lastDl) * 100 : 0

  const lastRev = data.actual_revenue?.[data.actual_revenue.length - 1]?.value || 0
  const forecastEndRev = data.revenue_forecast?.[data.revenue_forecast.length - 1]?.value || 0
  const revGrowth = lastRev > 0 ? ((forecastEndRev - lastRev) / lastRev) * 100 : 0

  const growthTone = (data.growth_status || "").toLowerCase().includes("declin") ? "critical" : "good"

  return (
    <div>
      <ReportSectionHeader
        icon={Activity}
        eyebrow="AI Forecasting"
        title={appName ? `${appName} — 7-Day Forecast` : "7-Day Forecast"}
        action={<ReportBadge tone={growthTone}>{data.growth_status || "Unknown"}</ReportBadge>}
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
        <ReportStatTile
          label="Downloads Forecast"
          value={fmtNum(forecastEndDl)}
          delta={`${dlGrowth > 0 ? "+" : ""}${dlGrowth.toFixed(1)}%`}
          deltaDirection={dlGrowth >= 0 ? "up" : "down"}
          sub="vs. current"
        />
        <ReportStatTile
          label="Revenue Forecast"
          value={fmtCurrency(forecastEndRev)}
          delta={`${revGrowth > 0 ? "+" : ""}${revGrowth.toFixed(1)}%`}
          deltaDirection={revGrowth >= 0 ? "up" : "down"}
          sub="vs. current"
        />
        <ReportStatTile label="Download Uplift" value={`+${data.uplift?.downloads || 0}%`} sub="if optimized" />
      </div>

      <ReportCard style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <BarChart3 size={14} color={REPORT_COLORS.textMuted} />
          <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Downloads — Actual vs. Forecast</span>
        </div>
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dlChart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="reportDlGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={REPORT_COLORS.sequential[400]} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={REPORT_COLORS.sequential[400]} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: REPORT_COLORS.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fill: REPORT_COLORS.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} width={44} tickFormatter={fmtNum} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="actual" stroke={REPORT_COLORS.sequential[400]} strokeWidth={2} fill="url(#reportDlGrad)" dot={false} connectNulls={false} />
              <Area type="monotone" dataKey="forecast" stroke={REPORT_COLORS.sequential[400]} strokeWidth={2} strokeDasharray="5 4" fill="url(#reportDlGrad)" dot={{ r: 3, fill: REPORT_COLORS.sequential[400] }} connectNulls={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </ReportCard>

      {(data.suggestions?.length > 0 || data.insights?.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
          {data.suggestions?.length > 0 && (
            <ReportCard>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <Lightbulb size={14} color={REPORT_COLORS.warning} />
                <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Suggestions</span>
              </div>
              {data.suggestions.slice(0, 3).map((s, i) => (
                <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, paddingBottom: 8, borderBottom: i < 2 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                  <strong style={{ color: REPORT_COLORS.textPrimary }}>{s.metric}: </strong>
                  {s.issue} — {s.action}
                </div>
              ))}
            </ReportCard>
          )}
          {data.insights?.length > 0 && (
            <ReportCard>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                {dlGrowth >= 0 ? <TrendingUp size={14} color={REPORT_COLORS.good} /> : <TrendingDown size={14} color={REPORT_COLORS.critical} />}
                <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Key Insights</span>
              </div>
              {data.insights.slice(0, 3).map((s, i) => (
                <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8 }}>
                  {s}
                </div>
              ))}
            </ReportCard>
          )}
        </div>
      )}
    </div>
  )
}
